import type { TFunction } from "i18next";
import { AlertTriangle, Check, ChevronDown, Copy, Loader2, ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CaptainAvatar } from "./CaptainAvatar";
import { CaptainErrorBoundary } from "./CaptainErrorBoundary";
import { DisplayToolResult } from "./DisplayToolResult";
import { PhaseIndicator } from "./PhaseIndicator";
import type {
  CaptainMessage,
  DisplayResult,
  MessageGroup,
  ResponsePhase,
} from "../types";
import { tryParseDisplayResult } from "../utils/displayTool";
import { formatToolName, tryParseToolCall } from "../utils/toolCall";

import { Bubble, BubbleContent } from "@/components/ui/v2/bubble";
import { Marker, MarkerContent, MarkerIcon } from "@/components/ui/v2/marker";
import {
  Message,
  MessageAvatar,
  MessageContent,
} from "@/components/ui/v2/message";

// --- Grouping (single function for persisted messages) ---

function describeToolCall(name: string, t: TFunction): string {
  const key = `captain.tools.${name}`;
  const translated = t(key, { defaultValue: "" });
  return translated && translated !== key ? translated : formatToolName(name);
}

function groupPersistedMessages(messages: CaptainMessage[], t: TFunction): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let currentPhases: ResponsePhase[] = [];
  let thinkingContent = "";
  let thinkingDuration: number | undefined;

  const ensureUnderstandPhase = () => {
    if (!currentPhases.find((p) => p.id === "understand")) {
      currentPhases.unshift({ id: "understand", status: "complete", steps: [] });
    }
  };

  const flushPhases = () => {
    if (currentPhases.length > 0 || thinkingContent) {
      if (thinkingContent) ensureUnderstandPhase();
      groups.push({
        kind: "phases",
        phases: currentPhases,
        thinkingContent: thinkingContent || undefined,
        thinkingDurationSeconds: thinkingDuration,
      });
      currentPhases = [];
      thinkingContent = "";
      thinkingDuration = undefined;
    }
  };

  for (const msg of messages) {
    if (msg.role === "user") {
      flushPhases();
      groups.push({ kind: "user", message: msg });
      continue;
    }

    if (msg.messageType === "thinking") {
      thinkingContent += msg.content;
      thinkingDuration = msg.thinkingDurationSeconds ?? thinkingDuration;
      continue;
    }

    if (msg.messageType === "tool_call") {
      const tc = msg.toolCallParsed ?? tryParseToolCall(msg.content);
      if (!tc) continue;

      const category = tc.category
        ?? (tc.name.startsWith("Display") ? "display" : undefined)
        ?? (tc.name.startsWith("Knowledge") ? "knowledge" : undefined)
        ?? (tc.name.startsWith("Data") ? "data_operation" : undefined)
        ?? "domain";

      // Display tools render as rich UI or get dropped — never in phases
      if (category === "display") {
        if (tc.result) {
          flushPhases();
          const payload = tryParseDisplayResult(tc.result);
          if (payload) groups.push({ kind: "display", payload });
        }
        continue;
      }

      const phaseId = category === "knowledge" ? "understand" : "fetch";
      let phase = currentPhases.find((p) => p.id === phaseId);
      if (!phase) {
        phase = { id: phaseId, status: "complete", steps: [] };
        currentPhases.push(phase);
      }
      phase.steps.push({
        name: describeToolCall(tc.name, t),
        toolCategory: category,
        isComplete: true,
        result: tc.result,
      });
      continue;
    }

    if (msg.content?.trim()) {
      flushPhases();

      if (msg.isError) {
        groups.push({ kind: "error", code: "", message: msg.content });
      } else if (msg.status === "groundedness_warning") {
        groups.push({ kind: "groundedness", message: msg.content });
      } else {
        groups.push({ kind: "text", content: msg.content, isStreaming: false, createdAt: msg.createdAt });
      }
    }
  }

  flushPhases();
  return groups;
}

// --- Components ---

function useRelativeTimeFormatter() {
  const { t } = useTranslation();
  return (dateStr: string): string => {
    const diffSec = Math.round((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diffSec < 60) return t("captain.time.justNow");
    if (diffSec < 3600) return t("captain.time.minutesAgo", { count: Math.floor(diffSec / 60) });
    if (diffSec < 86400) return t("captain.time.hoursAgo", { count: Math.floor(diffSec / 3600) });
    return new Date(dateStr).toLocaleDateString();
  };
}

function PreparingIndicator({ phases, hasText, hasDisplays }: {
  phases: ResponsePhase[];
  hasText: boolean;
  hasDisplays: boolean;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const hasPhases = phases.length > 0;
  const allPhasesComplete = hasPhases && phases.every((p) => p.status === "complete");
  const noOutput = !hasText && !hasDisplays;

  // Show only when: phases exist + all complete + no text/displays yet
  // With a 1s debounce to avoid flashing during tool chains
  const shouldShow = allPhasesComplete && noOutput;

  useEffect(() => {
    if (!shouldShow) { setVisible(false); return; }
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  if (!visible) return null;

  return (
    <Marker role="status">
      <MarkerIcon>
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      </MarkerIcon>
      <MarkerContent>
        <span className="shimmer">{t("captain.phase.preparing")}</span>
      </MarkerContent>
    </Marker>
  );
}

function AssistantText({ content, isStreaming, createdAt }: { content: string; isStreaming?: boolean; createdAt?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const formatTime = useRelativeTimeFormatter();

  const handleCopy = () => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  return (
    <div className="group flex flex-col">
      <Bubble variant="muted" className={isStreaming ? "animate-in fade-in duration-150" : undefined}>
        <BubbleContent className="captain-markdown">
          <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
        </BubbleContent>
      </Bubble>
      {!isStreaming && (
        <div className="flex items-center gap-1.5 px-1 pt-1 opacity-0 transition-opacity group-hover:opacity-100">
          {createdAt && (
            <span className="text-[10px] text-muted-foreground/40">
              {formatTime(createdAt)}
            </span>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="rounded p-0.5 hover:bg-muted"
            title={t("captain.actions.copy")}
          >
            {copied ? (
              <Check className="size-3 text-muted-foreground/50" />
            ) : (
              <Copy className="size-3 text-muted-foreground/40" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function CaptainMessageList({
  messages,
  isStreaming,
  streamPhases,
  streamText,
  streamDisplays,
  streamThinkingContent,
  streamThinkingDuration,
  groundednessReplacement,
}: {
  messages: CaptainMessage[];
  isStreaming: boolean;
  streamPhases: ResponsePhase[];
  streamText: string;
  streamDisplays: DisplayResult[];
  streamThinkingContent: string;
  streamThinkingDuration?: number;
  groundednessReplacement?: string;
}) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const [isNearBottom, setIsNearBottom] = useState(true);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    isNearBottomRef.current = near;
    setIsNearBottom(near);
  }, []);

  useEffect(() => {
    if (isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, streamText, streamPhases, streamDisplays]);

  const persistedGroups = groupPersistedMessages(messages, t);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="relative flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4"
    >
      {persistedGroups.map((group, gi) => {
        if (group.kind === "user") {
          return (
            <Message key={`u-${gi}`} align="end">
              <MessageContent>
                <Bubble variant="default">
                  <BubbleContent className="whitespace-pre-wrap">
                    {group.message.content}
                  </BubbleContent>
                </Bubble>
              </MessageContent>
            </Message>
          );
        }

        if (group.kind === "phases") {
          return (
            <Message key={`p-${gi}`} align="start">
              <MessageAvatar><CaptainAvatar /></MessageAvatar>
              <MessageContent>
                <div className="flex flex-col gap-0.5">
                  {group.phases.map((phase) => (
                    <PhaseIndicator
                      key={phase.id}
                      phase={phase}
                      thinkingContent={phase.id === "understand" ? group.thinkingContent : undefined}
                      thinkingDurationSeconds={phase.id === "understand" ? group.thinkingDurationSeconds : undefined}
                    />
                  ))}
                </div>
              </MessageContent>
            </Message>
          );
        }

        if (group.kind === "display") {
          return (
            <CaptainErrorBoundary key={`d-${gi}`}>
              <DisplayToolResult payload={group.payload} />
            </CaptainErrorBoundary>
          );
        }

        if (group.kind === "error") {
          return (
            <Marker key={`e-${gi}`}>
              <MarkerIcon><ShieldAlert className="size-4 text-destructive" /></MarkerIcon>
              <MarkerContent className="text-destructive">{group.message}</MarkerContent>
            </Marker>
          );
        }

        if (group.kind === "groundedness") {
          return (
            <Marker key={`g-${gi}`}>
              <MarkerIcon><AlertTriangle className="size-4 text-warning" /></MarkerIcon>
              <MarkerContent className="text-muted-foreground italic">{group.message}</MarkerContent>
            </Marker>
          );
        }

        if (group.kind === "text") {
          return (
            <Message key={`t-${gi}`} align="start">
              <MessageAvatar><CaptainAvatar /></MessageAvatar>
              <MessageContent>
                <AssistantText content={group.content} createdAt={group.createdAt} />
              </MessageContent>
            </Message>
          );
        }

        return null;
      })}

      {/* Live streaming response */}
      {isStreaming && (
        <Message align="start">
          <MessageAvatar><CaptainAvatar isActive /></MessageAvatar>
          <MessageContent>
            {/* Phase indicators (reasoning is inside the understand phase) */}
            {streamPhases.length > 0 && (
              <div className="flex flex-col gap-0.5">
                {streamPhases.map((phase) => (
                  <PhaseIndicator
                    key={phase.id}
                    phase={phase}
                    thinkingContent={phase.id === "understand" ? streamThinkingContent : undefined}
                    thinkingDurationSeconds={phase.id === "understand" ? streamThinkingDuration : undefined}
                    isThinkingActive={phase.id === "understand" && !!streamThinkingContent && !streamThinkingDuration}
                  />
                ))}
              </div>
            )}

            {/* Streaming text */}
            {streamText && <AssistantText content={streamText} isStreaming />}

            {/* Display results */}
            {streamDisplays.map((display, i) => (
              <CaptainErrorBoundary key={display.callId ?? i}>
                <DisplayToolResult payload={display.payload} showAvatar={false} />
              </CaptainErrorBoundary>
            ))}

            {/* Groundedness replacement */}
            {groundednessReplacement && (
              <Marker>
                <MarkerIcon><AlertTriangle className="size-4 text-warning" /></MarkerIcon>
                <MarkerContent className="text-muted-foreground italic">
                  {t("captain.groundednessWarning")}
                </MarkerContent>
              </Marker>
            )}

            {/* Initial waiting state — shimmer while thinking */}
            {!streamThinkingContent && streamPhases.length === 0 && !streamText && (
              <Marker role="status">
                <MarkerIcon>
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </MarkerIcon>
                <MarkerContent>
                  <span className="shimmer">{t("captain.thinking")}</span>
                </MarkerContent>
              </Marker>
            )}

            {/* Preparing response — debounced, only shows after phases complete with no output for 1s */}
            <PreparingIndicator
              phases={streamPhases}
              hasText={!!streamText}
              hasDisplays={streamDisplays.length > 0}
            />
          </MessageContent>
        </Message>
      )}

      <div ref={bottomRef} />

      {/* "New messages" pill when scrolled up during streaming */}
      {!isNearBottom && isStreaming && (
        <button
          type="button"
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
          className="sticky bottom-2 mx-auto flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground shadow-sm hover:bg-muted"
        >
          <ChevronDown className="size-3" />
          {t("captain.actions.newMessages")}
        </button>
      )}
    </div>
  );
}
